import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sentry } from "@/lib/sentry";
import { useAuthStore } from "../store/auth";
import { Icon } from "@/components/ui/Icon";

interface EditNameDialogProps {
  open: boolean;
  onClose: () => void;
}

export function EditNameDialog({ open, onClose }: EditNameDialogProps) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const updateName = useAuthStore((s) => s.updateName);

  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  // Prefill with the current name each time the dialog opens.
  useEffect(() => {
    if (open) setValue(user?.name ?? "");
  }, [open, user?.name]);

  const trimmed = value.trim();
  const current = user?.name ?? "";
  const unchanged = trimmed === current.trim();

  const handleSave = async () => {
    if (saving || unchanged) return;
    setSaving(true);
    try {
      await updateName(trimmed.length > 0 ? trimmed : null);
      onClose();
    } catch (err) {
      // Network/offline failures are expected; only report unexpected ones.
      if (navigator.onLine) Sentry.captureException(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Icon name="edit" size={14} />
            {t("userMenu.editName")}
          </DialogTitle>
        </DialogHeader>

        <div className="px-4 py-4">
          <Input
            label={t("userMenu.nameLabel")}
            placeholder={t("userMenu.namePlaceholder")}
            value={value}
            maxLength={100}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
            }}
            autoFocus
          />
        </div>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={unchanged || saving}
            loading={saving}
          >
            {t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
